import { TextTypes, GabcHeader } from "react-exsurge";
import { defaultColor, defaultRubricColor } from "../utils/colors";

const defaultDocSize = {
  'in': {
    width: 6,
    height: 9,
    margin: 0.5,
    units: 'in',
  },
  'cm': {
    width: 14.8,
    height: 21,
    margin: 1.2,
    units: 'cm',
  }
};
export default class ChantDocument {
  static defaultFont = 'EB Garamond';
  static defaultHeader = `name: Untitled Document;
supertitle:;
title:;
subtitle:;
text-left:;
text-right:;
annotation:;
%%
`;
  static getDefaultChantDocumentSize(units) {
    if (!units || !/^(in|cm)$/.test(units)) {
      try {
        units = localStorage.units;
      } catch (exception) { }
      if (!/^(in|cm)$/.test(units)) {
        units = 'in';
      }
    }
    return defaultDocSize[units];
  }
  static getDefaultChantDocument(gabc = `${ChantDocument.defaultHeader}(c4)`, units) {
    const defaultDocSize = this.getDefaultChantDocumentSize(units)
    return {
      ...defaultDocSize,
      staffSize: 1,
      staffColor: defaultColor,
      rubricColor: defaultRubricColor,
      autoColor: true,
      horizontalSpacing: 1,
      verticalSpacing: 1,
      alignment: "english",
      dropCap: true,
      font: ChantDocument.defaultFont,
      gabc
    };
  }

  static getPixelMultiplier(units) {
    return units === 'cm' ? 37.8 : 96;
  }

  static getPointMultiplier(units) {
    return units === 'cm' ? 28.3465 : 72;
  }

  static getContentWidth(doc) {
    return (doc.width - 2 * doc.margin) * this.getPixelMultiplier(doc.units);
  }

  static getContentHeight(doc) {
    return (doc.height - 2 * doc.margin) * this.getPixelMultiplier(doc.units);
  }

  static getStaffMultiplier(doc) {
    return ChantDocument.getContentWidth(doc) / 13.5;
  }

  static getStaffSize(doc) {
    return doc.staffSize * ChantDocument.getStaffMultiplier(doc);
  }

  static getBaseFontSize(doc) {
    return (ChantDocument.getStaffSize(doc) * 3) / 8;
  }

  static getCalculatedProps(doc) {
    const chantWidth = ChantDocument.getContentWidth(doc);
    const contentHeight = ChantDocument.getContentHeight(doc);
    const staffSize = ChantDocument.getStaffSize(doc);
    const interSyllabicSpacing = 4 * doc.horizontalSpacing;
    const spaceBetweenSystems = doc.verticalSpacing;
    const spaceAboveLyrics = Math.max(0.5, doc.verticalSpacing * 0.7);
    const baseFontSize = staffSize / 2;
    const textStyles = {};
    // calculate all font sizes:
    for (let key of Object.keys(TextTypes)) {
      for (let suffix of ["Size", "Font", "Color", "Alignment"]) {
        let fullKey = key + suffix;
        if (fullKey in doc) {
          textStyles[key] = textStyles[key] || {};
          textStyles[key][suffix.toLowerCase()] = doc[fullKey];
        }
      }
    }
    const gabcHeader = new GabcHeader(doc.gabc);
    const {
      annotation,
      annotationArray,
      supertitle,
      title,
      subtitle,
      textLeft,
      textRight,
      staffLineCount,
    } = gabcHeader;

    return {
      baseFontSize,
      textStyles,
      title,
      supertitle,
      subtitle,
      textLeft,
      textRight,
      width: chantWidth,
      height: contentHeight,
      staffSize,
      staffColor: doc.staffColor,
      dividerColor: doc.dividerColor,
      rubricColor: doc.rubricColor,
      autoColor: doc.autoColor,
      interSyllabicSpacing,
      spaceBetweenSystems,
      spaceAboveLyrics,
      annotation: annotationArray || (annotation ? [annotation] : []),
      staffLineCount: Number(staffLineCount) || 4,
    };
  }

  static getTextType(doc, key) {
    const thisTextType = TextTypes[key],
      docKey = `${key}Size`,
      docValue = doc[docKey] || 1,
      baseFontSize = thisTextType.defaultSize
        ? thisTextType.defaultSize(ChantDocument.getBaseFontSize(doc))
        : null,
      calculatedFontSize = Number((baseFontSize * docValue).toFixed(1)),
      min = Math.round(baseFontSize * 0.5),
      max = Math.round(baseFontSize * 1.5),
      handleTextSizeChange = (calculated, handleChange) => {
        let percentage = calculated / baseFontSize;
        percentage = Math.max(percentage, 0.5);
        percentage = Math.min(percentage, 1.5);
        handleChange({ [docKey]: parseFloat(percentage) });
      };

    return {
      ...thisTextType,
      docKey,
      baseFontSize,
      calculatedFontSize,
      min,
      max,
      handleTextSizeChange,
      isDefaultValue: docValue === 1
    };
  }

  static getHandleStaffSizeChange(doc) {
    return (calculated, handleChange) => {
      let percentage = calculated / ChantDocument.getStaffMultiplier(doc);
      percentage = Math.max(percentage, 0.5);
      percentage = Math.min(percentage, 1.5);
      handleChange({ staffSize: parseFloat(percentage) });
    };
  }
}
